import { ComponentProps, ElementType, cloneElement, useState } from "react";

import { useDelayedLoading } from "@oneleet/utils/src/hooks";

import { type VariantProps, cva } from "class-variance-authority";
import { AnimatePresence, motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

import { Loader } from "@oneleet/components/src/atoms";

const DEFAULT_VARIANT = "plain";
const DEFAULT_COLOR = "neutral";
const DEFAULT_SIZE = "medium";
const DEFAULT_IS_DISABLED = false;

const iconButton = cva(
  "flex items-center justify-center rounded-md text-sm font-semibold transition-colors duration-300 hover:duration-0",
  {
    variants: {
      variant: {
        solid: "",
        outlined: "",
        plain: "",
        ghost: "",
      },
      color: {
        accent: "",
        neutral: "",
        red: "",
      },
      size: {
        xxs: "p-1",
        tiny: "p-1",
        small: "p-1.5",
        medium: "p-[.4375rem]",
        large: "p-[.5625rem]",
      },
      disabled: {
        true: "cursor-default opacity-50",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "solid",
        color: "accent",
        className:
          "bg-accent-800 hover:data-[disabled=false]:bg-accent-900 text-white",
      },
      {
        variant: "outlined",
        color: "neutral",
        className:
          "bg-white text-gray-600 shadow-[0px_0px_0px_1px_rgba(35,34,34,0.08),0px_1px_1px_0px_rgba(35,34,34,0.05),0px_2px_4px_0px_rgba(35,34,34,0.08)] hover:data-[disabled=false]:bg-gray-100",
      },
      {
        variant: "plain",
        color: "neutral",
        className:
          "text-neutral-550 hover:text-neutral-650 bg-transparent hover:data-[disabled=false]:bg-neutral-100",
      },
      {
        variant: "ghost",
        color: "neutral",
        className:
          "text-neutral-450 hover:text-neutral-550 bg-transparent hover:data-[disabled=false]:bg-neutral-100",
      },
      {
        variant: "solid",
        color: "red",
        className:
          "bg-ng-red-800 hover:data-[disabled=false]:bg-ng-red-900 text-white",
      },
    ],
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      color: DEFAULT_COLOR,
      size: DEFAULT_SIZE,
      disabled: DEFAULT_IS_DISABLED,
    },
  },
);

const iconClassNames = cva("", {
  variants: {
    size: {
      xxs: "size-3",
      tiny: "size-4",
      small: "size-4",
      medium: "size-[1.125rem]",
      large: "size-[1.125rem]",
    },
  },
});

const loader = cva("", {
  variants: {
    variant: {
      solid: "",
      outlined: "",
      plain: "",
      ghost: "",
    },
    color: {
      accent: "",
      neutral: "",
      red: "",
    },
    size: {
      xxs: "size-3",
      tiny: "size-4",
      small: "size-4",
      medium: "size-[1.125rem]",
      large: "size-[1.125rem]",
    },
  },
  compoundVariants: [
    {
      variant: "solid",
      color: "accent",
      className: "text-accent-300 fill-white",
    },
    {
      variant: ["outlined", "plain", "ghost"],
      color: "neutral",
      className: "fill-neutral-600 text-neutral-300",
    },
    {
      variant: "solid",
      color: "red",
      className: "text-ng-red-300 fill-white",
    },
  ],
});

type IconButtonVariantProps = VariantProps<typeof iconButton>;

interface NgIconButtonBaseProps extends IconButtonVariantProps {
  icon: React.ReactElement<{ className?: string }>;
  hoverIcon?: React.ReactElement<{ className?: string }>;
  /**
   * Automatically disables the button when true
   */
  isLoading?: boolean;
  className?: string;
  classNames?: {
    loader?: string;
  };
  onClick?: (event: React.MouseEvent) => void;
}

/**
 * Polymorphic component type that allows rendering as different HTML elements.
 * Use the `as` prop to specify the element (defaults to "button").
 * Example: <NgIconButton as="a" href="/path" icon={<Icon />} />
 */
export type NgIconButtonProps<C extends ElementType = "button"> =
  NgIconButtonBaseProps & {
    as?: C;
  } & Omit<ComponentProps<C>, keyof NgIconButtonBaseProps | "as">;

export const NgIconButton = <C extends ElementType = "button">({
  as,
  variant = DEFAULT_VARIANT,
  color = DEFAULT_COLOR,
  size = DEFAULT_SIZE,
  icon,
  hoverIcon,
  type = "button",
  disabled = DEFAULT_IS_DISABLED,
  isLoading = false,
  className,
  classNames,
  onClick,
  ...rest
}: NgIconButtonProps<C>) => {
  const Component = as || "button";
  const [isHovered, setIsHovered] = useState(false);

  const styledIcon = icon
    ? cloneElement(icon, {
        className: twMerge(iconClassNames({ size }), icon.props.className),
      })
    : null;

  const styledHoveredIcon = hoverIcon
    ? cloneElement(hoverIcon, {
        className: twMerge(iconClassNames({ size }), hoverIcon.props.className),
      })
    : null;

  const showLoading = useDelayedLoading({ isLoading });

  const isDisabled = disabled || showLoading;

  return (
    <Component
      type={Component === "button" ? type : undefined}
      className={twMerge(
        iconButton({
          variant,
          color,
          size,
          disabled: isDisabled,
          className,
        }),
      )}
      disabled={Component === "button" ? isDisabled : undefined}
      data-disabled={isDisabled}
      onClick={(event) => {
        if (isDisabled) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        onClick?.(event);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...rest}
    >
      <AnimatePresence>
        {!showLoading && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ animate: { duration: 0 }, exit: { duration: 0.3 } }}
          >
            {isHovered && hoverIcon ? styledHoveredIcon : styledIcon}
          </motion.div>
        )}
      </AnimatePresence>
      {showLoading && (
        <Loader
          classNames={{
            svg: twMerge(
              loader({ variant, color, size, className: classNames?.loader }),
            ),
          }}
        />
      )}
    </Component>
  );
};

NgIconButton.displayName = "NgIconButton";
